import React, { Component, useEffect, useState } from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import MetisMenu from "metismenujs";
import { connect } from "react-redux";
import { toggleSidebar } from "../../store/actions";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { useHistory, useLocation } from "react-router";
import SweetAlert from "react-bootstrap-sweetalert";

import { isAdmin, isAuthenticated } from "../../lib/oktaConfig";
import { getProposalURL } from "../../helpers/common";
import { useNavGuard } from "../../contexts/NavGuardContext";

const QUOTE = gql`
  query getCurrentQuote($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      CompanyName
      PrimaryContact
      ContactFirstname
      ContactSurname
      ContactEmail
      Created
      CreatedBy
      Updated
      UpdatedBy
      Status
      QuoteTypeId1
      QuoteTypeId2
      QuoteContractMonths1
      QuoteContractMonths2
      Quote1DiscountMonthly
      Quote1DiscountTypeMonthly
      Quote1DiscountHardware
      Quote1DiscountTypeHardware
      Quote1DiscountSetup
      Quote1DiscountTypeSetup
      Quote2DiscountMonthly
      Quote2DiscountTypeMonthly
      Quote2DiscountHardware
      Quote2DiscountTypeHardware
      Quote2DiscountSetup
      Quote2DiscountTypeSetup
      WorkflowStep
      Status
      AcceptedQuote
      CustomerId
      IsTemplate
      ContractModified
      ProposalModified
      CurrentVersionId
      QuoteVersions {
        VersionId
        Token
        File
        Timestamp
      }
    }
    Enums(type: "WorkflowStep") {
      data
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;


const SideNav = ({ user }) => {
  const location = useLocation();
  const history = useHistory();
  const queryVariable = !location.pathname.startsWith('/admin/')
    ? { QuoteId: location.pathname.split("/").pop() }
    : null;
  const currentQuoteParam = location.search
    ? { QuoteId: location.search.split("=").pop() }
    : null;
  const activeVariable = currentQuoteParam ?? queryVariable;
  const { data: currentQuoteData, refetch } = useQuery(QUOTE, {
    variables: activeVariable ?? { QuoteId: null },
    skip: !activeVariable,
  });
  const [updateQuote, { data: updatedQuoteData }] = useMutation(UPDATE_QUOTE);

  const { callGuard } = useNavGuard();
  const [workflowLink, setWorkflowLink] = useState(null);
  const [stepError, setStepError] = useState(null);
  const [disableQuote, setDisableQuote] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const isTemplate = !!currentQuoteData?.Quote?.IsTemplate;

  const enabledSteps = (() => {
    if (!currentQuoteData?.Quote) return [];
    const { Quote } = currentQuoteData;

    const quoteAccepted = Quote.AcceptedQuote !== null && !Quote.ProposalModified;
    const customerExists = quoteAccepted && !!Quote.CustomerId;
    const contractReady = customerExists && !Quote.ContractModified;
    const contractSigned = Quote.WorkflowStep === 'CUSTOMER_ACCEPTED';

    return currentQuoteData.Enums?.data?.filter(step => {
      switch (step) {
        case 'BILLING_CONTACT_CAF':
          return quoteAccepted;
        case 'CUSTOMISE_CAF':
        case 'PREFILL_CAF':
          return customerExists;
        case 'SEND_FOR_SIGNING':
          return contractReady;
        case 'CUSTOMER_ACCEPTED':
          return contractSigned;
        default:
          return true;
      }
    });
  })();

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  useEffect(() => {
    if (updatedQuoteData && workflowLink !== null) {
      history.replace(workflowLink);
    }
  }, [updatedQuoteData, workflowLink, history]);

  useEffect(() => {
    if (currentQuoteData && currentQuoteData.Quote) {
      const status = currentQuoteData.Quote.Status;
      const currentStep = currentQuoteData.Quote.WorkflowStep || "";
      const flowSteps = [
        "QUOTE_SETUP",
        "QUOTE_ITEMS",
        "QUOTE_RATES",
        "CUSTOMISE_PROPOSAL",
        "SEND_PROPOSAL",
      ];
      setDisableQuote(status === 'accepted');
      const { QuoteVersions = [] } = currentQuoteData.Quote;

      if (QuoteVersions && flowSteps.indexOf(currentStep) < 0) {
        setPreviewUrl(getProposalURL(currentQuoteData.Quote));
      } else {
        if (currentQuoteData.Quote.AcceptedQuote && QuoteVersions) {
          setPreviewUrl(getProposalURL(currentQuoteData.Quote));
        } else {
          setPreviewUrl("");
        }
      }
    }
  }, [currentQuoteData]);

  const goToWorkflowStep = (e, workflowStep, link) => {
    e.preventDefault();
    if (!enabledSteps.includes(workflowStep)) return;
    setWorkflowLink(null);
    if (!disableQuote) {
      if (location.pathname !== link) {
        const guarded = callGuard(
          () => updateWorkflowStep(workflowStep, link),
          () => {}
        );
        if (!guarded) updateWorkflowStep(workflowStep, link);
      }
    } else {
      history.push(link);
    }
  };

  const updateWorkflowStep = (workflowStep, link) => {
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: currentQuoteData.Quote.QuoteId,
          WorkflowStep: workflowStep,
        },
      },
    });
    setWorkflowLink(link);
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const adminURL = (url) => {
    if (isTemplate) return url;
    return currentQuoteParam &&
      currentQuoteParam.QuoteId &&
      Number.isInteger(parseInt(currentQuoteParam.QuoteId))
      ? `${url}?currentQuote=${currentQuoteParam.QuoteId}`
      : queryVariable &&
        queryVariable.QuoteId &&
        Number.isInteger(parseInt(queryVariable.QuoteId))
      ? `${url}?currentQuote=${queryVariable.QuoteId}`
      : url;
  };

  const previewQuote = () => {
    window.open(previewUrl, "_blank");
  };

  const quoteId = currentQuoteData?.Quote?.QuoteId;

  const quoteSteps = {
    QUOTE_SETUP: {
      label: isTemplate ? 'Template Setup' : 'Quote Setup',
      path: isTemplate ? `/template-setup/${quoteId}` : `/quote-setup/${quoteId}`,
      icon: 'fas fa-file-import',
    },
    QUOTE_ITEMS: {
      label: 'Quote Items',
      path: `/quote-items/${quoteId}`,
      icon: 'fas fa-clipboard-list',
    },
    QUOTE_RATES: {
      label: 'Usage Rates',
      path: `/quote-rates/${quoteId}`,
      icon: 'fas fa-file-invoice-dollar',
    },
    CUSTOMISE_PROPOSAL: {
      label: 'Customise Proposal',
      path: `/customise-proposal/${quoteId}`,
      icon: 'fas fa-th-list',
    },
    SEND_PROPOSAL: {
      label: isTemplate ? 'Preview Template' : 'Send Proposal',
      path: isTemplate ? `/preview-template/${quoteId}` : `/send-proposal/${quoteId}`,
      icon: 'fas fa-file-signature',
    },
  };

  const cafSteps = {
    BILLING_CONTACT_CAF: {
      label: 'Billing Account',
      path: `/billing-contact-caf/${quoteId}`,
      icon: 'fas fa-address-card',
    },
    CUSTOMISE_CAF: {
      label: 'Customise CAF',
      path: `/customise-caf/${quoteId}`,
      icon: 'fas fa-newspaper',
    },
    PREFILL_CAF: {
      label: 'Prefill CAF',
      path: `/prefill-caf/${quoteId}`,
      icon: 'fas fa-pen-to-square',
    },
    SEND_FOR_SIGNING: {
      label: 'Send for Signing',
      path: `/send-for-signing/${quoteId}`,
      icon: 'fas fa-paper-plane',
    },
    CUSTOMER_ACCEPTED: {
      label: 'Customer Accepted',
      path: `/customer-accepted/${quoteId}`,
      icon: 'fas fa-paper-plane',
    },
  };

  const quoteLinks = [
    {
      label: 'My Quotes',
      path:  '/my-quotes',
      icon:  'ion ion-md-person',
    },
    {
      label: 'All Quotes',
      path:  '/quotes',
      icon:  'ion ion-ios-people',
    },
    {
      label: 'New Quote',
      path:  '/new-quote',
      icon:  'ion ion-md-add-circle-outline',
    },
  ];

  const adminLinks = [
    {
      label: 'Products',
      path:  '/admin/products',
      icon:  'ion ion-md-barcode',
    },
    {
      label: 'Categories',
      path:  '/admin/categories',
      icon:  'ion ion-ios-list',
    },
    {
      label: 'Attributes',
      path:  '/admin/attributes',
      icon:  'fas fa-tags',
    },
    {
      label: 'Bundles',
      path:  '/admin/bundles',
      icon:  'fas fa-layer-group',
    },
    {
      label: 'Quote Templates',
      path:  '/admin/quote-templates',
      icon:  'ion ion-ios-copy',
    },
    {
      label: 'Proposal Templates',
      path:  '/admin/proposal-templates',
      icon:  'ion ion-ios-paper',
    },
    {
      label: 'CAF Sections',
      path:  '/admin/caf-sections',
      icon:  'fas fa-list-ul',
    },
    {
      label: 'CAF Templates',
      path:  '/admin/caf-form-builder',
      icon:  'fab fa-wpforms',
    },
    {
      label: 'Teams',
      path:  '/admin/manage-teams',
      icon:  'fas fa-users',
    },
    {
      label: 'People',
      path:  '/admin/manage-users',
      icon:  'fas fa-user',
    },
    {
      label: 'Referring Agents',
      path:  '/admin/manage-agents',
      icon:  'fas fa-user-secret',
    },
    {
      label: 'Partners',
      path:  '/admin/manage-partners',
      icon:  'fas fa-handshake',
    },
    {
      label: 'Reporting',
      path:  '/admin/reporting',
      icon:  'fas fa-chart-bar',
    },
    {
      label: 'Configuration',
      path:  '/admin/admin-config',
      icon:  'ion ion-ios-settings',
    },
  ];

  return (
    <div id="sidebar-menu">
      {stepError && stepError.error === true && (
        <SweetAlert
          error
          title="Error"
          onConfirm={(e) => setStepError({ error: false, message: "" })}
        >
          {stepError.message}
        </SweetAlert>
      )}
      <ul className="metismenu" id="side-menu">
        <li className="menu-title quote-builder-title rbd-pc-hero-blue">
          {isTemplate ? 'Template Builder' : 'Quote Builder'}
          {currentQuoteData && currentQuoteData.Quote === null && (
            <span className="d-block text-capitalize font-weight-normal mt-1">
              No Quote Loaded
            </span>
          )}
        </li>
        {currentQuoteData && currentQuoteData.Quote && (
          <>
            <li className="quote-builder-menus rbd-pc-hero-blue">
              {Object.entries(quoteSteps).map(([step, { label, path, icon }]) => {
                const className = [
                  isActive(path) && "active",
                  !enabledSteps.includes(step) && "disabled",
                  "waves-effect",
                ].filter(Boolean).join(" ");
                const linkEl = (
                  <Link to={""} onClick={(e) => goToWorkflowStep(e, step, path)} className={className}>
                    <i className={icon}></i>{" "}
                    <span> {label} </span>
                  </Link>
                );
                return step === 'SEND_PROPOSAL' ? (
                  <div key={step} className="accepted-quote-link">
                    {linkEl}
                    {previewUrl && currentQuoteData.Quote.AcceptedQuote && (
                      <button className="accepted-quote rbd-sc-teal" onClick={previewQuote}>
                        Accepted {currentQuoteData.Quote.AcceptedQuote}
                      </button>
                    )}
                  </div>
                ) : (
                  <React.Fragment key={step}>{linkEl}</React.Fragment>
                );
              })}
            </li>

            {!isTemplate && (
              <>
                <li className="menu-title quote-builder-title caf-builder-title rbd-sc-teal">
                  CAF Builder
                </li>
                <li className="quote-builder-menus caf-builder-menus rbd-sc-teal">
                  {Object.entries(cafSteps).map(([step, { label, path, icon }]) => (
                    <Link
                      key={step}
                      to={""}
                      onClick={(e) => goToWorkflowStep(e, step, path)}
                      className={[
                        isActive(path) && "active",
                        !enabledSteps.includes(step) && "disabled",
                        "waves-effect",
                      ].filter(Boolean).join(" ")}
                    >
                      <i className={icon}></i>{" "}
                      <span> {label} </span>
                    </Link>
                  ))}
                </li>
              </>
            )}
          </>
        )}

        <li className="menu-title">Quotes</li>
        <li className="main-menus">
          {quoteLinks.map(({ label, path, icon }) => (
            <Link key={path} to={path} className={`waves-effect${isActive(path) ? ' mm-active' : ''}`}>
              <i className={icon}></i>
              <span> {label} </span>
            </Link>
          ))}
        </li>

        {Boolean(isAuthenticated() && isAdmin()) && (
          <li className="menu-title">Administration</li>
        )}
        {Boolean(isAuthenticated() && isAdmin()) && (
          <li className="main-menus">
            {adminLinks.map(({ label, path, icon }) => (
              <Link key={path} to={adminURL(path)} className={`waves-effect${isActive(path) ? ' mm-active' : ''}`}>
                <i className={icon}></i>
                <span> {label} </span>
              </Link>
            ))}
          </li>
        )}
      </ul>
    </div>
  );
};

class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = { is_toggle: false };
  }

  componentDidMount() {
    new MetisMenu("#side-menu");
  }

  render() {
    return (
      <React.Fragment>
        <div className="left side-menu">
          {this.props.is_toggle ? (
            <PerfectScrollbar>
              <SideNav user={this.props.user} />
            </PerfectScrollbar>
          ) : (
            <SideNav />
          )}
          <div className="clearfix"></div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStatetoProps = (state) => {
  const { is_toggle } = state.Layout;
  const { user } = state.User;
  return { is_toggle, user };
};

export default withRouter(connect(mapStatetoProps, { toggleSidebar })(Sidebar));
